import { Flex, Tooltip, Typography } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import BatchDelete from "@/components/Elements/BatchDelete";
import Filters, {
  useSchedulingStatesFilter,
  useWithoutTemplateFilter,
} from "@/components/Elements/Filters";
import { useTypesAndSubTypes } from "@/components/Elements/Filters/useTypesAndSubTypes";
import ModelList from "@/components/Elements/ModelList";
import ProgressBar from "@/components/Elements/ProgressBar";
import TableTitleCell from "@/components/Elements/TableTitleCell";
import { TAB_KEYS, TAB_PARAM_KEYS } from "@/constants/TabKeys";
import {
  INTERVENTIONS_FILTERS,
  INTERVENTIONS_INCLUDES,
} from "@/entities/interventions";
import { useInterventionType } from "@/entities/referential/referential-intervention-types";
import { REFERENTIAl_STATES_ENDPOINTS } from "@/entities/referential/referential-states";
import {
  InterventionsBatchDissociate,
  InterventionDissociateField,
} from "@/features/Interventions/Dissociate";
import InterventionBreadcrumb from "@/modules/intervention/intervention-breadcrumb";
import {
  InterventionSchedulingIndicator,
  InterventionStateIndicator,
} from "@/modules/intervention/intervention-indicator";
import InterventionsBatchEdit from "@/modules/interventions/interventions-batch-edit";
import { InterventionsListTableRowActions } from "@/modules/interventions/interventions-list-table";
import { useListContext } from "@/modules/list";
import ListTable, { filterListTableColumns } from "@/modules/list-table";
import { useSidePanel } from "@/modules/side-panel";
import { StateTag } from "@/modules/state-tag";
import {
  getTableFilterQueryable,
  TABLE_FILTER_TEXT,
  useTableFilterReferentialStates,
} from "@/modules/table-filter";
import { TranslateCost } from "@/modules/translate";
import { IconIndicator } from "@/ui/icon-indicator";
import { format, fromNow } from "@/utilities/date";

const InterventionsTable = ({
  hiddenColumns = [],
  fixedColumns = true,
  showLocationColumn = false,
  showAssetColumn = false,
  showBatchMode = true,
  showActionsButtons = true,
  showExportButton = true,
  dissociate = undefined,
  ...listTableProps
}) => {
  const { t } = useTranslation();

  const { includes, fetchParams } = useListContext();

  const statesFilter = useTableFilterReferentialStates(
    REFERENTIAl_STATES_ENDPOINTS.INTERVENTION
  );
  const schedulingStatesFilter = useSchedulingStatesFilter();
  const withoutTemplateFilter = useWithoutTemplateFilter();
  const { openSidePanel } = useSidePanel();

  const { types = [], subTypes } = useInterventionType();
  const { typesFilter, subTypesFilter } = useTypesAndSubTypes(
    fetchParams[INTERVENTIONS_FILTERS.INTERVENTION_TYPE_ID]
  );

  const onBatchEditClick = (interventions) => {
    openSidePanel({
      title: t("intervention:form.edit.label"),
      component: <InterventionsBatchEdit interventions={interventions} />,
    });
  };

  const onBatchDissociateClick = (models) => {
    if (!dissociate) return;

    openSidePanel({
      title: t("intervention:batchDissociate:title"),
      component: (
        <InterventionsBatchDissociate
          interventionsModels={models}
          dissociateField={dissociate.field}
          dissociateModel={dissociate.model}
          invalidateKeys={dissociate.invalidateKeys}
        />
      ),
    });
  };

  const onBatchDeleteClick = (models) => {
    openSidePanel({
      title: t("intervention:delete.label"),
      component: <BatchDelete type="intervention" models={models} />,
    });
  };

  const batchProps = showBatchMode
    ? {
        onBatchEditClick,
        onBatchDeleteClick,
        onBatchDissociateClick: dissociate ? onBatchDissociateClick : undefined,
      }
    : undefined;

  const columns = [
    {
      title: t("global:name"),
      dataIndex: "name",
      render: (name, { id }) => (
        <TableTitleCell
          text={name}
          link={`/interventions/${id}`}
          maxWidth="40vw"
        />
      ),
      sorter: true,
      fixed: fixedColumns ? "left" : undefined,
      minWidth: 220,
      ...TABLE_FILTER_TEXT,
    },
    {
      title: t("global:type"),
      dataIndex: "intervention_type_id",
      render: (intervention_type_id) => (
        <Typography.Text>
          {types.find(({ id }) => id === intervention_type_id)?.name}
        </Typography.Text>
      ),
      ...typesFilter,
    },
    {
      title: t("global:subtype"),
      render: ({ intervention_type_id, intervention_sub_type_id }) => (
        <Typography.Text>
          {
            subTypes(intervention_type_id).find(
              ({ id }) => id === intervention_sub_type_id
            )?.name
          }
        </Typography.Text>
      ),
      key: "intervention_sub_type_id",
      ...subTypesFilter,
    },
    {
      title: t("global:status"),
      dataIndex: "state",
      render: (state, intervention) => (
        <Flex gap="small">
          <StateTag value={state} type="intervention" />
          <InterventionStateIndicator intervention={intervention} />
        </Flex>
      ),
      sorter: true,
      ...statesFilter,
    },
    {
      title: t("intervention:form.scheduledDate.label"),
      dataIndex: "scheduled_date",
      sorter: true,
      render: (value) => (
        <Tooltip title={format(value, "LLLL")}>{format(value)}</Tooltip>
      ),
    },
    {
      title: t("global:alert"),
      dataIndex: "scheduling_state",
      align: "center",
      render: (value) => (
        <InterventionSchedulingIndicator schedulingState={value} />
      ),
      sorter: true,
      ...schedulingStatesFilter,
    },
    {
      title: <IconIndicator type="withoutTemplate" />,
      dataIndex: "intervention_template_id",
      render: (value) =>
        !value && <IconIndicator type="withoutTemplate" title={value} />,
      ...withoutTemplateFilter,
    },
    {
      title: t("global:cost"),
      dataIndex: "cost",
      align: "right",
      sorter: true,
      render: (cost) => cost && <TranslateCost value={cost} />,
    },
    {
      title: t("intervention:form.progress.label"),
      dataIndex: "progress",
      sorter: true,
      ...Filters.progress,
      render: (progress) => <ProgressBar value={progress} />,
    },
    {
      title: t("intervention:recommendedBy.label"),
      dataIndex: "recommended_by",
      render: (recommended_by) =>
        recommended_by && (
          <Link
            role="link"
            aria-label={t("intervention:seeParentIntervention.label")}
            key={`recommended-by-${recommended_by.id}`}
            to={`/interventions/${recommended_by.id}`}
          >
            {recommended_by.name}
          </Link>
        ),
    },
    {
      title: t("intervention:recommendedInterventions.label"),
      dataIndex: "recommended_interventions_count",
      render: (recommended_interventions_count, { id }) =>
        recommended_interventions_count > 0 && (
          <Link
            role="link"
            aria-label={t("intervention:seeChildrenInterventions.label")}
            key={`recommended-interventions-count-${id}`}
            to={`/interventions/${id}?${TAB_PARAM_KEYS.CONTEXT_TAB_KEY}=${TAB_KEYS.INTERVENTIONS_RECOMMENDATIONS}`}
          >
            {recommended_interventions_count}
          </Link>
        ),
    },
    {
      title: t("global:location"),
      dataIndex: ["asset", "ancestors"],
      render: (_ancestors, intervention) => (
        <InterventionBreadcrumb
          intervention={intervention}
          include={["location"]}
          hideIcon
          ellipsis
          compact
        />
      ),
      key: "asset.location_id",
      filtersIfCan: "view.location",
      ...getTableFilterQueryable({ model: "locations" }),
      hidden: !showLocationColumn,
    },
    {
      title: t("global:asset"),
      dataIndex: "asset",
      render: (_asset, intervention) => (
        <InterventionBreadcrumb
          intervention={intervention}
          include={["asset"]}
          hideIcon
          ellipsis
          compact
        />
      ),
      sorter: true,
      key: "asset.name",
      ...TABLE_FILTER_TEXT,
      hidden: !showAssetColumn,
    },
    {
      title: t("global:project"),
      dataIndex: "project_id",
      render: (project_id, { project }) =>
        project ? (
          <Link to={`/projects/${project_id}`}>{project.name}</Link>
        ) : (
          ""
        ),
      filtersIfCan: "view.project",
      ...getTableFilterQueryable({ model: "projects" }),
      hidden: !includes.includes(INTERVENTIONS_INCLUDES.PROJECT),
    },
    {
      title: t("global:userPlural"),
      dataIndex: ["users", "id"],
      render: (id, { users }) => (
        <ModelList model="user" values={users} key={id} />
      ),
      filtersIfCan: "view.user",
      ...getTableFilterQueryable({ model: "users" }),
    },
    {
      title: t("global:lastUpdate"),
      dataIndex: "updated_at",
      sorter: true,
      render: (value) => (
        <Tooltip title={format(value, "LLLL")}>{fromNow(value)}</Tooltip>
      ),
    },
    {
      title: "",
      fixed: fixedColumns ? "right" : undefined,
      render: (intervention) => (
        <InterventionsListTableRowActions
          intervention={intervention}
          dissociateInfos={dissociate}
        />
      ),
      hidden: !showActionsButtons,
    },
  ];

  const filtredColumns = filterListTableColumns({
    columns,
    hiddenColumns,
  });

  return (
    <ListTable
      columns={filtredColumns}
      showExportButton={showExportButton}
      {...batchProps}
      {...listTableProps}
    />
  );
};

InterventionsTable.propTypes = {
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  fixedColumns: PropTypes.bool,
  showLocationColumn: PropTypes.bool,
  showAssetColumn: PropTypes.bool,
  showBatchMode: PropTypes.bool,
  showActionsButtons: PropTypes.bool,
  showExportButton: PropTypes.bool,
  dissociate: PropTypes.shape({
    model: PropTypes.object,
    field: InterventionDissociateField,
    invalidateKeys: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  }),
};

export default InterventionsTable;
